import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Download, FileCheck2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Import() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "fintr_sablon.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Şablon indirilemedi"); }
  };

  const doImport = async () => {
    if (!file) { toast.error("Dosya seçin"); return; }
    const fd = new FormData(); fd.append("file", file);
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/import/transactions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created} işlem içe aktarıldı`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 slide-up" data-testid="import-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2">Veri İçe Aktarma</div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Excel ile Toplu İşlem Girişi</h1>
        <p className="text-[#94A3B8] mt-1 text-sm">Hazır şablon ile gelir ve giderlerinizi toplu olarak yükleyin.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-elev p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
              <Download className="w-5 h-5 text-indigo-400"/>
            </div>
            <div>
              <div className="font-semibold">1. Şablonu İndir</div>
              <div className="text-xs text-[#94A3B8]">Örnek verilerle hazır Excel şablonu</div>
            </div>
          </div>
          <ul className="text-sm text-[#94A3B8] space-y-1 mb-4 list-disc list-inside">
            <li>Zorunlu sütunlar: <span className="mono text-white">tarih, tur, tutar</span></li>
            <li>Opsiyonel: <span className="mono text-white">para_birimi, aciklama, kategori, banka, cari</span></li>
            <li>Tür: <span className="mono text-white">gelir</span> veya <span className="mono text-white">gider</span></li>
          </ul>
          <Button data-testid="download-template-btn" onClick={downloadTemplate} variant="outline" className="w-full bg-transparent border-[#1E293B] hover:bg-indigo-500/10 hover:border-indigo-500/50">
            <Download className="w-4 h-4 mr-2"/>Şablonu İndir (XLSX)
          </Button>
        </div>

        <div className="card-elev p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Upload className="w-5 h-5 text-emerald-400"/>
            </div>
            <div>
              <div className="font-semibold">2. Dosyayı Yükle</div>
              <div className="text-xs text-[#94A3B8]">Doldurduğunuz XLSX dosyasını yükleyin</div>
            </div>
          </div>
          <label className="flex flex-col items-center justify-center gap-2 px-4 py-10 rounded-xl border-2 border-dashed border-[#1E293B] hover:border-emerald-500/50 cursor-pointer bg-[#0F172A] transition-colors">
            <Upload className="w-7 h-7 text-[#64748B]"/>
            <span className="text-sm text-[#94A3B8]">{file ? file.name : "Dosya seçin veya buraya sürükleyin"}</span>
            <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="import-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)}/>
          </label>
          <Button data-testid="import-submit-btn" onClick={doImport} disabled={!file || loading} className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600">
            {loading ? "Yükleniyor…" : "İçe Aktar"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="card-elev p-6" data-testid="import-result">
          <div className="flex items-center gap-3 mb-4">
            <FileCheck2 className="w-6 h-6 text-emerald-400"/>
            <div>
              <div className="font-semibold">{result.created} işlem başarıyla eklendi</div>
              {result.errors.length > 0 && <div className="text-sm text-amber-400">{result.errors.length} satırda hata</div>}
            </div>
          </div>
          {result.errors.length > 0 && (
            <div className="space-y-1 mt-3 max-h-60 overflow-y-auto">
              {result.errors.map((e, i) => (
                <div key={i} className="text-xs text-amber-400 flex items-center gap-2 py-1 px-2 bg-amber-500/10 rounded">
                  <AlertCircle className="w-3 h-3"/>Satır {e.row}: {e.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
